import React, { useEffect, useState } from 'react';
import FormShell from './FormShell';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { useToast } from '../../hooks/use-toast';
import { loadSavedForm, saveForm } from './formUtils';

const DEFAULTS = {
  companyName: '',
  registrationNumber: '',
  sarsTaxNumber: '',
  officerFullName: '',
  officerIdNumber: '',
  officerEmail: '',
  officerPhone: '',
  officerTaxNumber: '',
  officerResidentialAddress: '',
  appointmentDate: '',
  notes: '',
};

const steps = ['Company', 'Public Officer', 'Review'];

const PublicOfficerForm = ({ serviceId, onComplete }) => {
  const { toast } = useToast();
  const [step, setStep] = useState(1);
  const [form, setForm] = useState(() => loadSavedForm(serviceId) || DEFAULTS);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    saveForm(serviceId, form);
    setSaved(true);
    const t = setTimeout(() => setSaved(false), 1500);
    return () => clearTimeout(t);
  }, [serviceId, form]);

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const validate = () => {
    if (step === 1) {
      if (!form.companyName || !form.registrationNumber || !form.sarsTaxNumber) {
        toast({
          title: 'Required Fields Missing',
          description: 'Company name, registration number and SARS tax number are required.',
          variant: 'destructive',
        });
        return false;
      }
    }
    if (step === 2) {
      if (
        !form.officerFullName ||
        !form.officerIdNumber ||
        !form.officerEmail ||
        !form.officerTaxNumber
      ) {
        toast({
          title: 'Required Fields Missing',
          description:
            'Officer name, ID, email and personal tax number are required.',
          variant: 'destructive',
        });
        return false;
      }
    }
    return true;
  };

  const next = () => {
    if (!validate()) return;
    setStep((s) => Math.min(s + 1, steps.length));
    window.scrollTo(0, 0);
  };
  const prev = () => {
    setStep((s) => Math.max(s - 1, 1));
    window.scrollTo(0, 0);
  };
  const submit = () => {
    if (!validate()) return;
    onComplete(form);
  };

  return (
    <FormShell
      title="Public Officer Appointment"
      stepLabels={steps}
      currentStep={step}
      onNext={next}
      onPrevious={prev}
      onSubmit={submit}
      autoSaved={saved}
    >
      {step === 1 && (
        <div className="space-y-4">
          <h3 className="text-xl font-semibold text-emerald-800">Company Information</h3>
          <div>
            <Label>Registered Company Name *</Label>
            <Input
              value={form.companyName}
              onChange={(e) => set('companyName', e.target.value)}
            />
          </div>
          <div className="grid md:grid-cols-2 gap-3">
            <div>
              <Label>CIPC Registration Number *</Label>
              <Input
                value={form.registrationNumber}
                onChange={(e) => set('registrationNumber', e.target.value)}
              />
            </div>
            <div>
              <Label>Company SARS Tax Number *</Label>
              <Input
                value={form.sarsTaxNumber}
                onChange={(e) => set('sarsTaxNumber', e.target.value)}
              />
            </div>
          </div>
          <div>
            <Label>Effective Appointment Date</Label>
            <Input
              type="date"
              value={form.appointmentDate}
              onChange={(e) => set('appointmentDate', e.target.value)}
            />
          </div>
        </div>
      )}

      {step === 2 && (
        <div className="space-y-4">
          <h3 className="text-xl font-semibold text-emerald-800">
            Proposed Public Officer
          </h3>
          <div>
            <Label>Full Name *</Label>
            <Input
              value={form.officerFullName}
              onChange={(e) => set('officerFullName', e.target.value)}
            />
          </div>
          <div className="grid md:grid-cols-2 gap-3">
            <div>
              <Label>ID / Passport Number *</Label>
              <Input
                value={form.officerIdNumber}
                onChange={(e) => set('officerIdNumber', e.target.value)}
              />
            </div>
            <div>
              <Label>Personal SARS Tax Number *</Label>
              <Input
                value={form.officerTaxNumber}
                onChange={(e) => set('officerTaxNumber', e.target.value)}
              />
            </div>
          </div>
          <div className="grid md:grid-cols-2 gap-3">
            <div>
              <Label>Email *</Label>
              <Input
                type="email"
                value={form.officerEmail}
                onChange={(e) => set('officerEmail', e.target.value)}
              />
            </div>
            <div>
              <Label>Phone</Label>
              <Input
                value={form.officerPhone}
                onChange={(e) => set('officerPhone', e.target.value)}
              />
            </div>
          </div>
          <div>
            <Label>Residential Address</Label>
            <textarea
              value={form.officerResidentialAddress}
              onChange={(e) => set('officerResidentialAddress', e.target.value)}
              className="mt-1 w-full p-2 border border-gray-300 rounded-md min-h-[80px]"
            />
          </div>
          <div>
            <Label>Additional Notes</Label>
            <textarea
              value={form.notes}
              onChange={(e) => set('notes', e.target.value)}
              className="mt-1 w-full p-2 border border-gray-300 rounded-md min-h-[60px]"
            />
          </div>
        </div>
      )}

      {step === 3 && (
        <Card className="bg-emerald-50 border-emerald-200">
          <CardHeader>
            <CardTitle className="text-emerald-800">Review</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 text-sm">
            <p><strong>Company:</strong> {form.companyName}</p>
            <p><strong>Reg No:</strong> {form.registrationNumber}</p>
            <p><strong>Company Tax No:</strong> {form.sarsTaxNumber}</p>
            <p><strong>Officer:</strong> {form.officerFullName}</p>
            <p><strong>Officer ID:</strong> {form.officerIdNumber}</p>
            <p><strong>Officer Email:</strong> {form.officerEmail}</p>
            <p><strong>Effective:</strong> {form.appointmentDate || 'ASAP'}</p>
          </CardContent>
        </Card>
      )}
    </FormShell>
  );
};

export default PublicOfficerForm;
